import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AxiosResponse } from 'axios';
import { Button } from 'react-bootstrap';
import apiDefinitions from '../../../../core/api/apiDefinitions';
import { showError, showSuccessUpdateAlert } from '../../../../core/alert/sweetAlertConfig';
import { all_routes } from '../../../router/all_routes';

type ScheduleType = '' | 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'YEARLY' | 'CUSTOM';
type ItemStatus = 'ACTIVE' | 'INACTIVE' | 'COMPLETED';

interface Item {
  id: number;
  itemName: string;
  brandId: number;
  description: string;
  capacity: string;
  itemTypeId: number;
  buildingId: number;
  floorId: number;
  roomId: number;

  // lifecycle/status
  serialNumber?: string;
  purchaseDate?: string;     // "YYYY-MM-DD"
  usingSinceDate?: string;   // "YYYY-MM-DD"
  warrantyEndDate?: string;  // "YYYY-MM-DD"
  status?: ItemStatus;

  // schedule (optional in existing data)
  isServiceRequired?: boolean;
  scheduleType?: ScheduleType;
  startDate?: string;        // "YYYY-MM-DD"
  endDate?: string;          // "YYYY-MM-DD"
  intervalMonths?: number | null;
}

interface ItemBrand { id: number; brandName: string; description: string; }
interface ItemType { id: number; itemType: string; description: string; }

interface LocationDTO {
  id: number; name: string; address: string; lat: number; lng: number;
  description?: string; createdBy: string; createdAt: string; updatedBy?: string; updatedAt?: string;
}
interface BuildingDTO {
  id: number; name: string; noOfFloors: number; locationName: string; locationId: number; description?: string | null;
}
interface FloorDTO {
  id: number; name: string; floorNumber: number; buildingId: number; buildingName: string; locationName: string;
  createdBy: string; createdAt: string; updatedBy?: string; updatedAt?: string;
}
interface RoomDTO {
  id: number; name: string; floorId: number; buildingId: number; status: string;
  createdBy: string; createdAt: string; updatedBy?: string; updatedAt?: string; description?: string;
}

interface UpdateItemModuleProps {
  show: boolean;
  onHide: () => void;
  item: Item | null;
  onSuccess: () => void;
  locations: LocationDTO[];
  buildings: BuildingDTO[];
  floors: FloorDTO[];
  rooms: RoomDTO[];
  itemBrands: ItemBrand[];
  itemTypes: ItemType[];
}

const UpdateItemModule: React.FC<UpdateItemModuleProps> = ({
  show,
  onHide,
  item,
  onSuccess,
  locations,
  buildings,
  floors,
  rooms,
  itemBrands,
  itemTypes,
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  // local editable copy + sane defaults
  const [editItem, setEditItem] = useState<Item | null>(null);

  useEffect(() => {
    if (!item) { setEditItem(null); return; }
    setEditItem({
      ...item,
      isServiceRequired: item.isServiceRequired ?? false,
      scheduleType: (item.scheduleType as ScheduleType) ?? '',
      startDate: item.startDate ?? '',
      endDate: item.endDate ?? '',
      intervalMonths: item.intervalMonths ?? null,
      serialNumber: item.serialNumber ?? '',
      purchaseDate: item.purchaseDate?.slice(0,10) ?? '',     // normalize to YYYY-MM-DD if ISO given
      usingSinceDate: item.usingSinceDate?.slice(0,10) ?? '',
      warrantyEndDate: item.warrantyEndDate?.slice(0,10) ?? '',
      status: (item.status as ItemStatus) ?? 'ACTIVE',
    });
  }, [item]);

  const todayStr = useMemo(() => new Date().toISOString().slice(0, 10), []);

  // derive locationId to show in dropdown from selected building/floor/room
  const selectedLocationId = (() => {
    if (!editItem) return '';
    const b = buildings.find(x => x.id === editItem.buildingId);
    if (b?.locationId) return String(b.locationId);
    if (editItem.floorId) {
      const f = floors.find(x => x.id === editItem.floorId);
      const fb = f && buildings.find(x => x.id === f.buildingId);
      if (fb?.locationId) return String(fb.locationId);
    }
    if (editItem.roomId) {
      const r = rooms.find(x => x.id === editItem.roomId);
      const rb = r && buildings.find(x => x.id === r.buildingId);
      if (rb?.locationId) return String(rb.locationId);
    }
    return '';
  })();

  /* ------------------- Cascading selects ------------------- */
  const handleLocationChange = async (locationId: string) => {
    if (!editItem) return;
    setEditItem({ ...editItem, buildingId: 0, floorId: 0, roomId: 0 });
    if (locationId) {
      try {
        setLoading(true);
        const res: AxiosResponse = await apiDefinitions.getBuildingsByLocationId(Number(locationId));
        if (res.status === 200) {
          buildings.length = 0;
          buildings.push(...(res.data.data || []));
        } else {
          showError(res.data?.statusDescription || 'Failed to fetch buildings');
        }
      } catch (e: any) {
        showError(e.response?.data?.statusDescription || 'Error fetching buildings');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleBuildingChange = async (buildingId: string) => {
    if (!editItem) return;
    setEditItem({ ...editItem, buildingId: Number(buildingId), floorId: 0, roomId: 0 });
    if (buildingId) {
      try {
        setLoading(true);
        const res: AxiosResponse = await apiDefinitions.getFloorsByBuildingId(Number(buildingId));
        if (res.status === 200) {
          floors.length = 0;
          floors.push(...(res.data.data || []));
        } else {
          showError(res.data?.statusDescription || 'Failed to fetch floors');
        }
      } catch (e: any) {
        showError(e.response?.data?.statusDescription || 'Error fetching floors');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleFloorChange = async (floorId: string) => {
    if (!editItem) return;
    setEditItem({ ...editItem, floorId: Number(floorId), roomId: 0 });
    if (floorId) {
      try {
        setLoading(true);
        const res: AxiosResponse = await apiDefinitions.getRoomsByFloorId(Number(floorId));
        if (res.status === 200) {
          rooms.length = 0;
          rooms.push(...(res.data.data || []));
        } else {
          showError(res.data?.statusDescription || 'Failed to fetch rooms');
        }
      } catch (e: any) {
        showError(e.response?.data?.statusDescription || 'Error fetching rooms');
      } finally {
        setLoading(false);
      }
    }
  };

  /* ------------------- Validations ------------------- */
  const isPastOrToday = (d?: string) => !d || d <= todayStr;
  const isFutureOrToday = (d?: string) => !d || d >= todayStr;

  const validateBeforeSubmit = (): boolean => {
    if (!editItem) return false;

    if (!editItem.itemName?.trim() || !editItem.description?.trim() || !editItem.brandId || !editItem.itemTypeId) {
      showError('Item name, description, brand, and type are required.');
      return false;
    }
    if (!(editItem.roomId || editItem.floorId || editItem.buildingId)) {
      showError('Please select a building, floor, or room.');
      return false;
    }

    // lifecycle dates
    if (editItem.purchaseDate && !isPastOrToday(editItem.purchaseDate)) {
      showError('Purchase Date cannot be in the future.');
      return false;
    }
    if (editItem.usingSinceDate && !isPastOrToday(editItem.usingSinceDate)) {
      showError('Using Since Date cannot be in the future.');
      return false;
    }
    if (editItem.warrantyEndDate && !isFutureOrToday(editItem.warrantyEndDate)) {
      showError('Warranty End Date must be today or a future date.');
      return false;
    }

    // schedule
    if (editItem.isServiceRequired) {
      if (!editItem.scheduleType) {
        showError('Schedule type is required when service is required.');
        return false;
      }
      if (!editItem.startDate) {
        showError('Start date is required when service is required.');
        return false;
      }
      if (editItem.endDate && !(editItem.endDate > editItem.startDate)) {
        showError('End date must be after start date.');
        return false;
      }
      if (editItem.scheduleType === 'CUSTOM') {
        if (editItem.intervalMonths == null || Number(editItem.intervalMonths) < 1) {
          showError('Interval months must be a positive number for Custom schedule.');
          return false;
        }
      }
    }

    return true;
  };

  /* ------------------- Submit ------------------- */
  const handleUpdateItem = async () => {
    if (!editItem) return;
    if (!validateBeforeSubmit()) return;

    const token = localStorage.getItem('token');
    if (!token) {
      showError('No authentication token found. Please log in.');
      navigate(all_routes.login);
      return;
    }

    try {
      setLoading(true);

      // Build payload to mirror backend Item builder
      const payload: any = {
        id: editItem.id,
        itemName: editItem.itemName.trim(),
        brandId: editItem.brandId,
        description: editItem.description,
        capacity: editItem.capacity || '',
        itemTypeId: editItem.itemTypeId,
        isServiceRequired: !!editItem.isServiceRequired,

        // lifecycle / status
        serialNumber: editItem.serialNumber || '',
        purchaseDate: editItem.purchaseDate ? `${editItem.purchaseDate}T00:00:00Z` : null,
        usingSinceDate: editItem.usingSinceDate ? `${editItem.usingSinceDate}T00:00:00Z` : null,
        warrantyEndDate: editItem.warrantyEndDate ? `${editItem.warrantyEndDate}T00:00:00Z` : null,
        status: (editItem.status as ItemStatus) || 'ACTIVE',
      };

      if (editItem.isServiceRequired) {
        payload.scheduleType = editItem.scheduleType;
        payload.startDate = editItem.startDate; // "YYYY-MM-DD"
        if (editItem.endDate) payload.endDate = editItem.endDate;
        if (editItem.scheduleType === 'CUSTOM' && editItem.intervalMonths) {
          payload.intervalMonths = Number(editItem.intervalMonths);
        }
      }

      // location precedence: room > floor > building
      if (editItem.roomId) payload.roomId = editItem.roomId;
      else if (editItem.floorId) payload.floorId = editItem.floorId;
      else if (editItem.buildingId) payload.buildingId = editItem.buildingId;

      const response = await apiDefinitions.updateItem(payload);
      if (response.data.status === 'S1000') {
        showSuccessUpdateAlert('Item updated successfully');
        onSuccess();
        onHide();
      } else {
        showError(response.data.statusDescription || 'Failed to update item');
      }
    } catch (error: any) {
      if (error.response?.status === 401) {
        showError('Unauthorized access. Your session may have expired. Please log in again.');
        localStorage.removeItem('token');
        navigate(all_routes.login);
      } else {
        showError(error.response?.data?.statusDescription || 'Error updating item');
      }
    } finally {
      setLoading(false);
    }
  };

  /* ------------------- UI ------------------- */
  return (
    <div className={`modal fade ${show ? 'show d-block' : ''}`} role="dialog" style={{ background: show ? 'rgba(0,0,0,0.5)' : 'none' }}>
      {/* 3-column responsive grid helper */}
      <style>{`
        .grid-3 { display:grid; grid-template-columns: 1fr; gap: 12px; }
        @media (min-width: 992px) { .grid-3 { grid-template-columns: repeat(3, 1fr); } }
        .full-span { grid-column: 1 / -1; }
        .section-title { font-weight: 600; margin-bottom: .25rem; }
      `}</style>

      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content" style={{ borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
          <div className="modal-header" style={{ background: '#f8f9fa', borderBottom: '1px solid #e9ecef' }}>
            <h5 className="modal-title" style={{ color: '#333', fontSize: '1.1rem' }}>Edit Item</h5>
            <button className="btn-close" onClick={onHide} aria-label="Close" />
          </div>

          <div className="modal-body" style={{ padding: '1.25rem' }}>
            {/* ---------- Basic Info ---------- */}
            <div className="section-title">Basic Info</div>
            <div className="grid-3">
              <div className="full-span">
                <label className="col-form-label">Item Name <span className="text-danger">*</span></label>
                <input
                  type="text"
                  className="form-control"
                  value={editItem?.itemName || ''}
                  onChange={(e) => setEditItem(prev => prev ? ({ ...prev, itemName: e.target.value }) : prev)}
                />
              </div>

              <div>
                <label className="col-form-label">Brand <span className="text-danger">*</span></label>
                <select
                  className="form-select"
                  value={editItem?.brandId || ''}
                  onChange={(e) => setEditItem(prev => prev ? ({ ...prev, brandId: Number(e.target.value) }) : prev)}
                >
                  <option value="">Select Brand</option>
                  {itemBrands.map((brand) => (
                    <option key={brand.id} value={brand.id}>{brand.brandName}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="col-form-label">Item Type <span className="text-danger">*</span></label>
                <select
                  className="form-select"
                  value={editItem?.itemTypeId || ''}
                  onChange={(e) => setEditItem(prev => prev ? ({ ...prev, itemTypeId: Number(e.target.value) }) : prev)}
                >
                  <option value="">Select Type</option>
                  {itemTypes.map((type) => (
                    <option key={type.id} value={type.id}>{type.itemType}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="col-form-label">Capacity</label>
                <input
                  type="text"
                  className="form-control"
                  value={editItem?.capacity || ''}
                  onChange={(e) => setEditItem(prev => prev ? ({ ...prev, capacity: e.target.value }) : prev)}
                />
              </div>

              <div className="full-span">
                <label className="col-form-label">Description <span className="text-danger">*</span></label>
                <input
                  type="text"
                  className="form-control"
                  value={editItem?.description || ''}
                  onChange={(e) => setEditItem(prev => prev ? ({ ...prev, description: e.target.value }) : prev)}
                />
              </div>

              <div>
                <label className="col-form-label">Serial Number</label>
                <input
                  type="text"
                  className="form-control"
                  value={editItem?.serialNumber ?? ''}
                  onChange={(e) => setEditItem(prev => prev ? ({ ...prev, serialNumber: e.target.value }) : prev)}
                />
              </div>
            </div>

            <hr className="my-3" />

            {/* ---------- Location ---------- */}
            <div className="section-title">Location</div>
            <div className="grid-3">
              <div>
                <label className="col-form-label">Location <span className="text-danger">*</span></label>
                <select
                  className="form-select"
                  value={selectedLocationId}
                  onChange={(e) => handleLocationChange(e.target.value)}
                >
                  <option value="">Select Location</option>
                  {locations.map((loc) => (
                    <option key={loc.id} value={loc.id}>{loc.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="col-form-label">Building <span className="text-danger">*</span></label>
                <select
                  className="form-select"
                  value={editItem?.buildingId || ''}
                  onChange={(e) => handleBuildingChange(e.target.value)}
                  disabled={!buildings.length}
                >
                  <option value="">Select Building</option>
                  {buildings.map((building) => (
                    <option key={building.id} value={building.id}>{building.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="col-form-label">Floor</label>
                <select
                  className="form-select"
                  value={editItem?.floorId || ''}
                  onChange={(e) => handleFloorChange(e.target.value)}
                  disabled={!floors.length}
                >
                  <option value="">{floors.length ? 'Select Floor' : 'Select a building first'}</option>
                  {floors.map((floor) => (
                    <option key={floor.id} value={floor.id}>{floor.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="col-form-label">Room</label>
                <select
                  className="form-select"
                  value={editItem?.roomId || ''}
                  onChange={(e) => setEditItem(prev => prev ? ({ ...prev, roomId: Number(e.target.value) }) : prev)}
                  disabled={!rooms.length}
                >
                  <option value="">{rooms.length ? 'Select Room' : 'Select a floor first'}</option>
                  {rooms.map((room) => (
                    <option key={room.id} value={room.id}>{room.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <hr className="my-3" />

            {/* ---------- Lifecycle & Status ---------- */}
            <div className="section-title">Lifecycle & Status</div>
            <div className="grid-3">
              <div>
                <label className="col-form-label">Purchase Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={editItem?.purchaseDate || ''}
                  max={todayStr}
                  onChange={(e) => setEditItem(p => p ? ({ ...p, purchaseDate: e.target.value }) : p)}
                />
              </div>
              <div>
                <label className="col-form-label">Using Since Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={editItem?.usingSinceDate || ''}
                  max={todayStr}
                  onChange={(e) => setEditItem(p => p ? ({ ...p, usingSinceDate: e.target.value }) : p)}
                />
              </div>
              <div>
                <label className="col-form-label">Warranty End Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={editItem?.warrantyEndDate || ''}
                  min={todayStr}
                  onChange={(e) => setEditItem(p => p ? ({ ...p, warrantyEndDate: e.target.value }) : p)}
                />
              </div>
              <div>
                <label className="col-form-label">Status <span className="text-danger">*</span></label>
                <select
                  className="form-select"
                  value={(editItem?.status as ItemStatus) || 'ACTIVE'}
                  onChange={(e) => setEditItem(p => p ? ({ ...p, status: e.target.value as ItemStatus }) : p)}
                >
                  <option value="ACTIVE">ACTIVE</option>
                  <option value="INACTIVE">INACTIVE</option>
                  <option value="COMPLETED">COMPLETED</option>
                </select>
              </div>
            </div>

            <hr className="my-3" />

            {/* ---------- Service ---------- */}
            <div className="section-title">Service</div>
            <div className="form-check form-switch mb-2">
              <input
                id="isServiceRequired"
                className="form-check-input"
                type="checkbox"
                checked={!!editItem?.isServiceRequired}
                onChange={(e) =>
                  setEditItem(prev => prev ? ({
                    ...prev,
                    isServiceRequired: e.target.checked,
                    ...(e.target.checked ? {} : { scheduleType: '', startDate: '', endDate: '', intervalMonths: null })
                  }) : prev)
                }
              />
              <label className="form-check-label ms-2" htmlFor="isServiceRequired">Service required</label>
            </div>

            {editItem?.isServiceRequired && (
              <div className="grid-3">
                <div>
                  <label className="col-form-label">Schedule Type <span className="text-danger">*</span></label>
                  <select
                    className="form-select"
                    value={editItem.scheduleType || ''}
                    onChange={(e) => {
                      const val = e.target.value as ScheduleType;
                      setEditItem(prev => prev ? ({
                        ...prev,
                        scheduleType: val,
                        intervalMonths: val === 'CUSTOM' ? (prev.intervalMonths ?? 1) : null
                      }) : prev);
                    }}
                  >
                    <option value="">Select Schedule</option>
                    <option value="DAILY">Daily</option>
                    <option value="WEEKLY">Weekly</option>
                    <option value="MONTHLY">Monthly</option>
                    <option value="YEARLY">Yearly</option>
                    <option value="CUSTOM">Custom (every N months)</option>
                  </select>
                </div>

                <div>
                  <label className="col-form-label">Start Date <span className="text-danger">*</span></label>
                  <input
                    type="date"
                    className="form-control"
                    value={editItem.startDate || ''}
                    onChange={(e) => setEditItem(prev => prev ? ({ ...prev, startDate: e.target.value }) : prev)}
                    min={todayStr}
                  />
                </div>

                <div>
                  <label className="col-form-label">End Date (optional)</label>
                  <input
                    type="date"
                    className="form-control"
                    value={editItem.endDate || ''}
                    onChange={(e) => setEditItem(prev => prev ? ({ ...prev, endDate: e.target.value }) : prev)}
                    min={editItem.startDate || todayStr}
                  />
                </div>

                {editItem.scheduleType === 'CUSTOM' && (
                  <div>
                    <label className="col-form-label">Interval (months) <span className="text-danger">*</span></label>
                    <input
                      type="number"
                      className="form-control"
                      min={1}
                      step={1}
                      placeholder="e.g., 3"
                      value={editItem.intervalMonths ?? ''}
                      onChange={(e) => {
                        const n = e.target.value === '' ? null : Number(e.target.value);
                        setEditItem(prev => prev ? ({ ...prev, intervalMonths: n }) : prev);
                      }}
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="modal-footer" style={{ background: '#f8f9fa', borderTop: '1px solid #e9ecef' }}>
            <Button variant="primary" onClick={onHide}>Cancel</Button>
            <Button variant="success" onClick={handleUpdateItem} disabled={loading}>
              {loading ? <span className="spinner-border spinner-border-sm me-2" /> : 'Save'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpdateItemModule;
